import { type ReactNode, useCallback } from "react";
import IconButton from "@/shared/components/generic/button/IconButton.tsx";
import IconMagnifierCheck from "@/shared/components/generic/icons/IconMagnifierCheck.tsx";
import { Level, type Size } from "@/shared/components/generic/UiEnums.ts";
import { ScheduleAlertsDialog } from "@/pages/human-resources/time-management/components/schedule-alerts/ScheduleAlertsDialog.tsx";
import useScheduleViewStateDispatch from "@/pages/human-resources/time-management/model/schedule-view/useScheduleViewStateDispatch.ts";
import useScheduleViewState from "@/pages/human-resources/time-management/model/schedule-view/useScheduleViewState.ts";
import { useFetchSchedulesAlerts } from "@/pages/human-resources/time-management/services/hooks/useFetchSchedulesAlerts.ts";
import useVisibleEmployeesSchedules from "@/pages/human-resources/time-management/model/schedule-view/useVisibleEmployeesSchedules.ts";
import BasicTooltipWithOffset from "@/pages/human-resources/time-management/components/BasicTooltipWithOffset.tsx";
import { useTranslation } from "react-i18next";

interface Properties {
  buttonSize: Size;
  disabled: boolean;
}

export default function ScheduleAlertsOpener(props: Properties): ReactNode {
  const { t } = useTranslation(["humanResources"], {
    keyPrefix: "timeManagement",
  });
  const { alertsDialogState } = useScheduleViewState();
  const { dispatch } = useScheduleViewStateDispatch();
  const fetchAlerts = useFetchSchedulesAlerts();
  const visibleSchedules = useVisibleEmployeesSchedules();

  const fetchAndShowAlerts = useCallback((): void => {
    dispatch({
      type: "openAlertsDialog",
      alerts: undefined,
      verifiedSchedules: visibleSchedules,
    });
    fetchAlerts(visibleSchedules)
      .then((alerts) => {
        dispatch({
          type: "openAlertsDialog",
          alerts,
          verifiedSchedules: visibleSchedules,
        });
        return;
      })
      .catch((err: unknown) => {
        console.error(err);
        setTimeout(() => {
          dispatch({ type: "closeAlertsDialog" });
        }, 1000);
      });
  }, [dispatch, fetchAlerts, visibleSchedules]);

  return (
    <>
      <BasicTooltipWithOffset title={t("scheduleAlerts.verifySchedule")}>
        <IconButton
          icon={IconMagnifierCheck}
          level={Level.TERTIARY}
          size={props.buttonSize}
          onClick={fetchAndShowAlerts}
          disabled={props.disabled}
        />
      </BasicTooltipWithOffset>
      {alertsDialogState.open && <ScheduleAlertsDialog />}
    </>
  );
}
